import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Eye, CalendarPlus, ClipboardList, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BADGE_META = {
  tarea_vencida:       { label: 'Tarea vencida',       color: 'bg-red-100 text-red-700' },
  sin_visita_90d:      { label: 'Sin visita +90d',     color: 'bg-amber-100 text-amber-700' },
  oportunidad_abierta: { label: 'Oportunidad abierta', color: 'bg-emerald-100 text-emerald-700' },
  contrato_vence_12m:  { label: 'Contrato vence <12m', color: 'bg-yellow-100 text-yellow-700' },
};

export default function MapaMobileBottomSheet({ resumen, onClose }) {
  const navigate = useNavigate();

  return (
    <>
      {/* Overlay translúcido */}
      <div
        className="fixed inset-0 bg-black/20 z-[1001]"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[1002] bg-white rounded-t-2xl shadow-2xl p-5 space-y-4"
        style={{ maxHeight: '70vh', overflowY: 'auto' }}
        data-testid="entity-bottom-sheet"
      >
        {/* Handle + cerrar */}
        <div className="flex items-center justify-between">
          <div className="w-10 h-1 rounded-full bg-ink-300 mx-auto absolute left-1/2 -translate-x-1/2 top-3" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-ink-900 text-base leading-tight">{resumen.nombre}</div>
            <div className="text-xs text-ink-500 mt-0.5">
              {resumen.tipo} · {resumen.estado}
              {resumen.provincia && ` · ${resumen.provincia}`}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-ink-100 text-ink-400 shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Badges */}
        {resumen.badges?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {resumen.badges.map((b) => {
              const m = BADGE_META[b];
              return m ? (
                <span key={b} className={`px-2 py-1 rounded-full text-xs font-medium ${m.color}`}>
                  {m.label}
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Contadores */}
        <div className="grid grid-cols-3 gap-2 text-center bg-ink-50 rounded-xl p-3">
          {[
            { label: 'Visitas',    val: resumen.num_visitas },
            { label: 'Tareas',     val: resumen.num_tareas },
            { label: 'Contratos',  val: resumen.num_contratos_activos },
          ].map(({ label, val }) => (
            <div key={label}>
              <div className="text-2xl font-bold text-ink-900">{val ?? 0}</div>
              <div className="text-xs text-ink-400">{label}</div>
            </div>
          ))}
        </div>

        {/* Última visita */}
        {resumen.ultima_visita && (
          <div className="flex items-center gap-2 text-sm">
            <Clock size={14} className="text-ink-400 shrink-0" />
            <span className="text-ink-500">Última visita:</span>
            <span className="text-ink-700 font-medium">
              {resumen.ultima_visita.fecha
                ? new Date(resumen.ultima_visita.fecha).toLocaleDateString('es-ES')
                : '—'}
            </span>
          </div>
        )}

        {/* Próxima tarea */}
        {resumen.proxima_tarea && (
          <div className="flex items-start gap-2 text-sm">
            <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <div className="text-ink-700 font-medium">{resumen.proxima_tarea.titulo}</div>
              {resumen.proxima_tarea.fecha_limite && (
                <div className="text-xs text-ink-400">
                  {new Date(resumen.proxima_tarea.fecha_limite).toLocaleDateString('es-ES')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Acciones — botones grandes para táctil */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            className="h-12 text-sm"
            onClick={() => navigate(`/clientes/${resumen.id}`)}
          >
            <Eye size={15} className="mr-2" /> Ver ficha
          </Button>
          {resumen.telefono ? (
            <Button
              className="h-12 text-sm"
              variant="outline"
              onClick={() => window.open(`tel:${resumen.telefono}`)}
            >
              <Phone size={15} className="mr-2" /> Llamar
            </Button>
          ) : (
            <Button
              className="h-12 text-sm"
              variant="outline"
              onClick={() => toast.info('Próximamente — registrar visita desde el mapa')}
            >
              <CalendarPlus size={15} className="mr-2" /> Visita
            </Button>
          )}
          <Button
            variant="outline"
            className="h-12 text-sm col-span-2"
            onClick={() => navigate('/tareas')}
          >
            <ClipboardList size={15} className="mr-2" /> Crear tarea
          </Button>
        </div>
      </div>
    </>
  );
}
